import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { take } from 'rxjs/operators';

export interface PWAInstallationState {
  isSupported: boolean;
  canInstall: boolean;
  isInstalled: boolean;
  isMobile: boolean;
  isIOS: boolean;
  isStandalone: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PWAService {
  private deferredPrompt: any = null;
  private installationState = new BehaviorSubject<PWAInstallationState>({
    isSupported: false,
    canInstall: false,
    isInstalled: false,
    isMobile: false,
    isIOS: false,
    isStandalone: false
  });

  public installationState$ = this.installationState.asObservable();

  constructor() {
    this.initializeService();
  }

  private initializeService(): void {
    if (typeof window !== 'undefined' && 'navigator' in window) {
      this.updateInstallationState();
      this.setupBeforeInstallPromptListener();
      this.setupAppInstalledListener();
    }
  }

  private updateInstallationState(): void {
    const state: PWAInstallationState = {
      isSupported: this.isPWASupported(),
      canInstall: this.deferredPrompt !== null,
      isInstalled: this.isAppInstalled(),
      isMobile: this.isMobileDevice(),
      isIOS: this.isIOSDevice(),
      isStandalone: this.isStandaloneMode()
    };

    this.installationState.next(state);
  }

  private isPWASupported(): boolean {
    return 'serviceWorker' in navigator && 'BeforeInstallPromptEvent' in window;
  }

  private isAppInstalled(): boolean {
    // Check if app is already installed
    return this.isStandaloneMode() || 
           window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
  }

  private isStandaloneMode(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
  }

  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(navigator.userAgent);
  }

  private isIOSDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  private setupBeforeInstallPromptListener(): void {
    fromEvent(window, 'beforeinstallprompt').subscribe((event: any) => {
      event.preventDefault();
      this.deferredPrompt = event;
      this.updateInstallationState();
    });
  }

  private setupAppInstalledListener(): void {
    fromEvent(window, 'appinstalled').subscribe(() => {
      this.deferredPrompt = null;
      this.updateInstallationState();
    });
  }

  public async installPWA(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false;
    }

    try {
      this.deferredPrompt.prompt();
      const choiceResult = await this.deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        this.deferredPrompt = null;
        this.updateInstallationState();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error installing PWA:', error);
      return false;
    }
  }

  public shouldShowInstallPrompt(): boolean {
    const state = this.installationState.value;
    return state.isMobile && 
           !state.isInstalled && 
           (state.canInstall || state.isIOS) &&
           !this.hasUserDismissedPrompt();
  }

  public dismissInstallPrompt(): void {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }

  private hasUserDismissedPrompt(): boolean {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (!dismissed) return false;

    // Show again after 7 days
    const dismissedTime = parseInt(dismissed);
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    return dismissedTime > sevenDaysAgo;
  }

  public getIOSInstallInstructions(): string[] {
    return [
      'Trykk på Del-knappen (firkant med pil oppover)',
      'Velg "Legg til på hjemskjerm"',
      'Trykk "Legg til" for å installere appen'
    ];
  }
}
